import Cast from '@/helpers/Cast';
import Device from '@/helpers/Device';
import DeviceSettings from '@/helpers/DeviceSettings';
import DeviceClock from '@/helpers/DeviceClock';
import LgScreen from '@/helpers/LgScreen';
import CloneUpload from '@/helpers/CloneUpload';
import StorageProbe from '@/helpers/StorageProbe';
import DeviceType from '@/helpers/DeviceType';
import Diagnostics from '@/helpers/Diagnostics';
import MediaHandler from '@/helpers/MediaHandler';
import Registration from '@/helpers/Registration';
import Requests from '@/helpers/Request';
import { SOCKET_ACTIONS } from '@/helpers/SocketHandler';
import StorageHandler, { STORE_KEY, STORE_VALUE } from '@/helpers/StorageHandler';
import StorageInspector from '@/helpers/StorageHandler/Inspector';
import Download, { fileNameFromUrl } from '@/helpers/Download';
import TV from '@/helpers/TV';
import ForeignApp from '@/helpers/ForeignApp';
import AppBuild from '@/helpers/AppBuild';
import { standbyRebootPlanned } from './cast';
import router from '@/router';

// run_channel: der Socket-Admin adressiert einen Sender tolerant ueber eines
// der Felder id / position / serviceId / epgChannelId (Feldnamen defensiv, da
// je nach Admin-Version unterschiedlich). Liefert den Index in der (paket-
// gefilterten) Kanalliste oder -1, wenn kein Kanal passt.
function findChannelIndex(channels, ctx) {
  if (!ctx || !Array.isArray(channels)) return -1;
  const matchers = [
    { field: 'id', keys: ['id', 'channelId', 'channel_id'] },
    { field: 'position', keys: ['position', 'channelPosition', 'programme'] },
    { field: 'serviceId', keys: ['serviceId', 'service_id'] },
    { field: 'epgChannelId', keys: ['epgChannelId', 'epg_channel_id', 'epgId'] },
  ];
  for (const m of matchers) {
    let val;
    for (const k of m.keys) {
      if (ctx[k] !== undefined && ctx[k] !== null && ctx[k] !== '') {
        val = ctx[k];
        break;
      }
    }
    if (val === undefined) continue;
    // epgChannelId == 0 ist bei vielen Kanaelen der Default -> nicht eindeutig.
    if (m.field === 'epgChannelId' && String(val) === '0') continue;
    const idx = channels.findIndex((ch) => ch && String(ch[m.field]) === String(val));
    if (idx !== -1) return idx;
  }
  return -1;
}

// ---------------------------------------------------------------------------
// Live-Log tunnel
// An admin opens it via ["tunnel","log:open",<deviceId>]; the socket-server
// forwards ["log:open"] to us and relays anything we emit as ["log:open", …]
// back to that admin. We stream console output back until ["log:close"] arrives.
// The wire protocol is arrays only, so every frame we send is [event, ...args].
// ---------------------------------------------------------------------------
const LOG_CONSOLE_METHODS = ['log', 'info', 'warn', 'error'];
let logSocket = null; // current socket used to stream log lines
let originalConsole = null; // saved console fns while forwarding is active
let forwarding = false; // reentrancy guard so our own send() logs don't recurse

function fmtLogArg(a) {
  if (typeof a === 'string') return a;
  if (a instanceof Error) return a.stack || a.message;
  try {
    return JSON.stringify(a);
  } catch (e) {
    return String(a);
  }
}

function startLogForward() {
  if (originalConsole) return; // already forwarding
  originalConsole = {};
  LOG_CONSOLE_METHODS.forEach((method) => {
    originalConsole[method] = console[method];
    console[method] = function(...args) {
      originalConsole[method].apply(console, args);
      if (forwarding) return;
      if (!logSocket || logSocket.readyState !== WebSocket.OPEN) return;
      forwarding = true;
      try {
        const line = args.map(fmtLogArg).join(' ');
        logSocket.send(
          JSON.stringify(['log:open', { level: method, line, ts: Date.now() }])
        );
      } catch (e) {
        // never throw from an overridden console method
      } finally {
        forwarding = false;
      }
    };
  });
  originalConsole.log.call(console, 'Live-Log tunnel opened');
}

function stopLogForward() {
  if (!originalConsole) return;
  const saved = originalConsole;
  LOG_CONSOLE_METHODS.forEach((method) => {
    console[method] = saved[method];
  });
  originalConsole = null;
}

// ---------------------------------------------------------------------------
// Device state reporting
// The device pushes ["state", { screen, activity, language }] to the
// socket-server on connect and whenever one of those changes. The server
// stamps it, keeps the last snapshot (getDeviceState) and notifies admins.
// The heavier Info-page snapshot is only gathered on demand (get_info).
// ---------------------------------------------------------------------------
const ACTIVITY_MAP = {
  Home: 'Home',
  TVPlayer: 'TV',
  RadioPlayer: 'Radio',
  SourceSelect: 'SourceSelect',
  InputSource: 'InputSource',
  EPGGuide: 'EPG',
  Vod: 'VOD',
  VodSubMenu: 'VOD',
  SubVodMenu: 'VOD',
  SubMenu: 'Menu',
  WelcomePage: 'Welcome',
  Registration: 'Registration',
  Info: 'Info',
  Demo: 'Demo',
  Cast: 'Cast',
};

// Den Bildschirmzustand direkt am Geraet nachfragen - synchron, ohne Umweg
// ueber den gemerkten POWER_STATE. Rueckgabe: 'on' | 'off' | null (keine API
// oder keine verwertbare Antwort).
//
// NUR LESEND. POWER_STATE wird hier bewusst NICHT geschrieben: der Merker wird
// an mehreren Stellen symmetrisch gepflegt (remotepower-/b2bapis-Listener,
// PowerStateWrapper, Power-Taste) und die WGT-Startdatei haengt daran. Diese
// Funktion fasst nichts davon an, sie schaut nur nach.
//
// Zugriff direkt auf window statt ueber Device.apis: dessen Getter schreibt bei
// jedem Zugriff console.log-Zeilen, und die sind auf TV-WebViews teuer.
// Dieselbe Abkuerzung nimmt isRealStandby() in keyHandler/Tizen.js.
function readLivePowerState() {
  try {
    const power =
      (window.b2bapis && window.b2bapis.b2bpower) ||
      (window.webapis && window.webapis.remotepower);
    if (!power || typeof power.getPowerState !== 'function') return null;
    const state = String(power.getPowerState() || '').toUpperCase();
    // Beide Schreibweisen, die auch die Power-Listener kennen: b2bapis liefert
    // 'Normal'/'Standby', webapis.remotepower Varianten mit STANDBY bzw. OFF.
    if (state.indexOf('STANDBY') !== -1 || state.indexOf('OFF') !== -1) return 'off';
    if (state.indexOf('NORMAL') !== -1) return 'on';
    return null;
  } catch (e) {
    return null;
  }
}

// Woher der zuletzt gemeldete Bildschirm-Zustand stammt - und ob er gemessen
// oder nur angenommen ist.
//
// Der Anlass: readScreen endet mit 'on', wenn keine Quelle etwas hergibt. Im
// Admin sah eine Annahme damit genauso aus wie eine Messung, und auf einem
// US662 (webOS 4.5) stand dauerhaft "an", obwohl das Geraet den Zustand gar
// nicht meldet. Der Wert selbst bleibt wie er ist - alte Anzeigen brechen
// nicht -, aber die Zustandsmeldung sagt jetzt dazu, wie belastbar er ist.
const screenInfo = { source: 'annahme', known: false };

// Der zuletzt von der LG-Sonde GEMESSENE Bildschirmzustand.
//
// Nur fuer die Flanke 'off' -> 'on': daran haengt das Aufraeumen beim
// Aufwachen, siehe LgScreen.start() weiter unten.
let lastLgScreen = null;

function noteScreen(source, known) {
  screenInfo.source = source;
  screenInfo.known = known;
}

function readScreen() {
  try {
    if (typeof document !== 'undefined' && document.hidden) {
      noteScreen('visibilitychange', true);
      return 'off';
    }
  } catch (e) {
    /* ignore */
  }
  // LG: der gemessene Bildschirm-Zustand.
  //
  // Auf LG ist auf visibilitychange beim EINSCHLAFEN kein Verlass (siehe
  // main.js) und einen Power-Listener gibt es nicht - im Admin stand deshalb
  // dauerhaft "Bildschirm an", auch wenn der TV im Zimmer dunkel war. Die
  // Sonde in helpers/LgScreen haengt statt dessen am hcap-Ereignis
  // power_mode_changed und fragt, wo das Modell es kann, hcap.video.isVideoMute
  // ab. Antwortet die Firmware auf beides nicht - wie der 43UK660H, der
  // isVideoMute quittiert und keinen Modus meldet -, bleibt der Wert null und
  // es gilt weiter, was unten steht.
  try {
    if (DeviceType.isLG) {
      const lg = LgScreen.screen;
      if (lg) {
        noteScreen('LgScreen-Sonde', true);
        return lg;
      }
    }
  } catch (e) {
    /* ignore */
  }
  try {
    const v = StorageHandler.get(STORE_KEY.POWER_STATE);
    // Beim OFF nachfassen, sonst nicht.
    //
    // Der Merker wird asynchron geschrieben (auf Tizen ueber tizen.keymanager,
    // und dessen getData liefert bis zum Ruecklauf von saveData noch den alten
    // Wert). Die Zustandsmeldung an den Socket haengt dagegen an
    // visibilitychange - beim Aufwachen las readScreen also noch das alte OFF,
    // und im SocketAdmin blieb "Standby" stehen, bis der Gast die naechste
    // Seite oeffnete. Ein 'ON' im Speicher ist dagegen nie zu frueh da: dann
    // bleibt es beim billigen Weg ohne zusaetzlichen Aufruf.
    if (v === STORE_VALUE.POWER_OFF) {
      noteScreen('Power-Merker', true);
      return readLivePowerState() || 'off';
    }
    if (v === STORE_VALUE.POWER_ON) {
      noteScreen('Power-Merker', true);
      return 'on';
    }
  } catch (e) {
    /* ignore */
  }
  // Keine Quelle hat geantwortet. 'on' bleibt der Rueckfall - ein Geraet, das
  // meldet, laeuft ja -, aber es ist eine ANNAHME und wird als solche
  // gekennzeichnet. Betroffen sind Modelle ohne Power-Listener und ohne
  // brauchbare Sonde, etwa ein US662 (webOS 4.5).
  noteScreen('annahme', false);
  return 'on';
}

function readActivity() {
  const route = router.currentRoute || {};
  const name = route.name || 'Home';
  if (name === 'MediaPlayer') {
    const t = route.query && route.query.type;
    if (t === 'WEBSITE') return 'Website';
    if (t === 'VIDEO') return 'Video';
    if (t === 'IMAGE') return 'Image';
    return 'Media';
  }
  return ACTIVITY_MAP[name] || name;
}

// Bricht ein Promise nach ms ab und liefert dann null.
//
// Notwendig fuer get_info: die Antwort IST der Ack des command-Kanals, und der
// laeuft nach einer Weile in "command response timed out" (4004). Die
// Cast-Erkennung fragt auf dem TV dutzende Properties ab und kann haengen, wenn
// die Firmware auf einen Callback verzichtet - dann ist eine unvollstaendige
// Antwort besser als gar keine.
const INFO_DETECT_TIMEOUT_MS = 6000;

// Zeitbudget fuer das Lesen/Setzen der geraeteinternen Einstellungen.
//
// Auf LG haengt an jedem Aufruf, den die Firmware nicht kennt, ein eigener
// Timeout (siehe helpers/DeviceSettings/LG.js) - ein UM767H antwortet auf
// solche Aufrufe gar nicht mehr. Die Abfragen laufen zwar nebenlaeufig, aber
// der command-Kanal wartet nicht ewig: laeuft es laenger, ist eine Antwort mit
// dem, was da ist, mehr wert als gar keine.
const SETTINGS_TIMEOUT_MS = 8000;

// Dasselbe INNERHALB von get_info - knapper.
//
// get_info hat sein Zeitbudget schon zur Haelfte an die Cast-Erkennung
// vergeben, und die Antwort IST der Ack des command-Kanals. Die Einstellungen
// fahren dort nur mit, weil sie meistens sofort da sind (auf Samsung sind die
// Aufrufe synchron); kommen sie nicht rechtzeitig, fehlen sie in der Info und
// der Admin holt sie mit get_settings nach.
const INFO_SETTINGS_TIMEOUT_MS = 3000;

// Die Uhr in get_info. Auf Samsung synchron; auf LG haengt an getLocalTime ein
// Callback, der auf manchen Modellen ausbleibt.
const INFO_CLOCK_TIMEOUT_MS = 2000;

// set_time: bis zu drei Wege, jeder mit eigenem Callback-Budget, dazu das
// Lesen vorher und nachher.
const SET_TIME_TIMEOUT_MS = 12000;

// Fortschritt wird gedrosselt gemeldet. Die Callbacks der Firmware- und
// Clone-APIs feuern dicht; bei einem Rollout ueber ein ganzes Haus waeren das
// hunderte Frames pro Minute auf dem Socket-Server. Phasenwechsel und der
// Abschluss gehen immer sofort raus, nur die Prozentschritte dazwischen warten.
const ROLLOUT_REPORT_MS = 3000;

// Pause zwischen dem Abraeumen und dem eigentlichen Reload (siehe runAppReload).
// Muss unter den 2000 ms des Reconnect-Timers in socket.onclose bleiben.
const APP_RELOAD_DELAY_MS = 400;
let lastRolloutReport = 0;

// Antwort auf einen Befehl. Gleicher Rahmen wie die get_info-Antwort - der
// Admin bekommt damit nicht nur ein "angekommen", sondern das Ergebnis.
/**
 * Erkennt die Kommando-Id, die der Socket-Server voranstellt.
 *
 * Uuid v1, so wie der Server sie vergibt. Steht sie da, ist das zweite
 * Element der Nachricht die Id und erst das dritte die Nutzlast.
 */
const UUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Die Id des Kommandos, das gerade beantwortet wird - je Aktion.
 *
 * WARUM UEBERHAUPT: ohne Id ordnet der Socket-Server Antworten nach dem
 * Windhundprinzip zu - aelteste offene Anfrage desselben Ereignisnamens
 * gewinnt. Alle Geraetekommandos heissen aber `experienceEvent`. Bleibt EIN
 * Kommando unbeantwortet (ein Neustart mitten im Abruf reicht), beantwortet
 * ab da jede Antwort das jeweils vorige Kommando, und das jeweils letzte
 * laeuft in "command response timed out" - ein Phantomfehler, der bis zum
 * naechsten Neustart der Verbindung bleibt. Mit der Id trifft jede Antwort
 * ihr eigenes Kommando.
 */
const commandIds = {};

/**
 * Das ERSTE ELEMENT des Rahmens, in dem das Kommando kam - je Aktion.
 *
 * Der Socket-Server kennt mehr als einen Rahmen, und die Antwort muss denselben
 * tragen wie die Frage, sonst findet sie ihr Kommando nicht:
 *
 *   ['experienceEvent', id, {action}]  die eingebauten Schaltflaechen des Admin
 *   ['clear_credentials', id, {…}]     am Geraet gemessen: die Aktion steht
 *                                      SELBST vorn, wenn das Kommando frei
 *                                      abgesetzt wird
 *
 * Deshalb wird hier genau dieses erste Element gemerkt und in sendReply bzw.
 * replyFrame wiederverwendet. Ohne Eintrag gilt 'experienceEvent', damit sich
 * am bisherigen Verhalten nichts aendert.
 */
const commandChannels = {};

/**
 * Den Antwortrahmen bauen und die Kommando-Id verbrauchen.
 *
 * Fuer die grossen Antworten (get_info, get_apps), die ihre Nutzlast nicht
 * durch `sendReply` schicken - deren Protokollzeile wuerde die komplette
 * Diagnose in das Live-Log kippen. Die Id gehoert trotzdem daran: ohne sie
 * ordnet der Socket-Server die Antwort dem falschen Kommando zu, und das
 * jeweils letzte laeuft in "command response timed out".
 */
function replyFrame(action, payload) {
  const commandId = commandIds[action];
  const channel = commandChannels[action] || 'experienceEvent';
  delete commandIds[action];
  delete commandChannels[action];
  return JSON.stringify(commandId ? [channel, commandId, payload] : [channel, payload]);
}

function sendReply(state, action, data) {
  try {
    if (!state.socket || state.socket.readyState !== WebSocket.OPEN) {
      console.log('[socket] Antwort zu ' + action + ' nicht gesendet - keine Verbindung');
      return false;
    }
    const payload = Object.assign({ action: action }, data || {});
    if (typeof payload.ok === 'undefined') payload.ok = true;
    const commandId = commandIds[action];
    const channel = commandChannels[action] || 'experienceEvent';
    const frame = commandId ? [channel, commandId, payload] : [channel, payload];
    state.socket.send(JSON.stringify(frame));
    // Die Id gilt fuer genau eine Antwort. Stehen lassen hiesse, dass eine
    // spaetere Meldung derselben Aktion ein fremdes Kommando quittiert.
    delete commandIds[action];
    delete commandChannels[action];
    console.log('[socket] Antwort zu ' + action + ':', JSON.stringify(payload));
    return true;
  } catch (e) {
    console.log('[socket] Antwort zu ' + action + ' nicht gesendet:', e);
    return false;
  }
}

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(null), ms)),
  ]);
}

function safeResolve(fn) {
  try {
    return Promise.resolve(fn()).catch(() => null);
  } catch (e) {
    return Promise.resolve(null);
  }
}

// Gather the Info-page snapshot (mix of live device-helper values + store).
//
// Der Admin soll fernab dasselbe sehen wie die Info-Seite am Geraet. Die
// Geraetefelder stehen flach in diesem Objekt, die Diagnose-Tabellen als
// `sections` [{ title, rows }] - beide aus @/helpers/Diagnostics, damit Seite
// und Fernabfrage nicht auseinanderlaufen.
//
// Was hier bewusst FEHLT: der DB-Vergleich in Klammern, den die Info-Seite
// zeigt. Der Admin hat den DB-Stand selbst und braucht vom Geraet nur den
// Live-Wert; ein zweites Mal durch die API zu gehen waere Ballast.
async function gatherInfo(rootState, castDiag) {
  const d = (rootState && rootState.device) || {};
  // Die Uhr nebenher lesen - auf LG ist das ein Callback, der nicht auf die
  // Einstellungen warten soll.
  const clockPromise = withTimeout(Promise.resolve(DeviceClock.read()).catch(() => null), INFO_CLOCK_TIMEOUT_MS);
  const [mac, ip, serialNumber, model, version, firmware, platform, fingerprint] = await Promise.all([
    safeResolve(() => Device.mac),
    safeResolve(() => Device.ip),
    safeResolve(() => Device.serialNumber),
    safeResolve(() => Device.model),
    safeResolve(() => Device.version),
    safeResolve(() => Device.firmware),
    safeResolve(() => Device.platform),
    safeResolve(() => Registration.getFingerprint()),
  ]);
  let installedApps = [];
  try {
    installedApps = (await Device.getInstalledApps()) || [];
  } catch (e) {
    installedApps = [];
  }
  // Die geraeteinternen Einstellungen fahren mit: der Admin zeigt sie im selben
  // Zug an und spart sich eine zweite Abfrage.
  //
  // OHNE die API-Aufzaehlung: die gehoert zur gezielten Abfrage (get_settings)
  // und traegt auf einem Samsung leicht hundert Methodennamen - in der ohnehin
  // grossen get_info-Antwort waere das nur Ballast.
  let settings = null;
  try {
    const full = await withTimeout(
      Promise.resolve(DeviceSettings.read()).catch(() => null),
      INFO_SETTINGS_TIMEOUT_MS
    );
    if (full) {
      settings = Object.assign({}, full);
      delete settings.api;
    }
  } catch (e) {
    settings = null;
  }
  // KEINE Speicher-Sonde hier.
  //
  // Sie stand kurz in get_info und hat einen U800F zum Neustart gebracht,
  // sobald auf dem Stick eine echte Datei lag. get_info ist das
  // meistbenutzte Kommando - was es anfasst, muss unbedenklich sein. Die
  // Sonde hat jetzt ihr eigenes Kommando: get_files.
  return {
    deviceId: d.deviceId,
    buildingId: d.buildingId,
    groupId: d.groupId,
    type: d.type,
    model: model || d.model,
    software: firmware || d.software,
    version: version || d.version,
    mac: mac || d.mac,
    ip: ip || d.ip,
    serialNumber: serialNumber || d.serialNumber,
    fingerprint: fingerprint || d.fingerprint,
    platform: platform || d.platform,
    resWidth: safeSync(() => Device.resolutionWidth),
    resHeight: safeSync(() => Device.resolutionHeight),
    appId: safeSync(() => Device.appId),
    packageId: safeSync(() => Device.packageId),
    installedAppsCount: installedApps.length,
    appVersion: process.env.APP_VERSION,
    // Der laufende Stand: Build-Id dieses Codes, die Id, unter der der Loader
    // ihn angefordert hat, und der Startzeitpunkt (siehe helpers/AppBuild).
    // appVersion oben aendert sich nicht und sagt darueber nichts.
    appBuild: AppBuild.build,
    loaderBuild: AppBuild.loaderBuild,
    loaderSource: AppBuild.loaderSource,
    appStartedAt: AppBuild.startedAt,
    // Letzter Fall einer Fremd-App ueber der App (siehe helpers/ForeignApp).
    // Steht auch in der state-Meldung; hier, weil der Admin den Status nach
    // einem Info-Abruf aus dieser Antwort neu aufbaut.
    foreignApp: ForeignApp.last(),
    // Steht beim naechsten Standby ein Cast-Neustart an? sessionActive bleibt
    // gesetzt, bis ein Neustart zum Loeschen wirklich gelaufen ist - jeder
    // Standby plant bis dahin neu (siehe cast/scheduleStandbyReboot).
    castState: rootState.cast
      ? {
          sessionActive: !!rootState.cast.sessionActive,
          clearPending: !!rootState.cast.clearPending,
          rebootPlanned: standbyRebootPlanned(),
        }
      : null,
    // Die installierte WGT (nur Samsung). appVersion ist der Stand dieser App
    // auf dem Server, die WGT das, was am Geraet installiert ist.
    wgtVersion: safeSync(() => Device.wgtVersion) || null,
    // APP_VERSION des Loaders - weicht bei alten Builds von wgtVersion ab
    // (config.xml nicht hochgezaehlt, siehe helpers/AppBuild).
    loaderVersion: AppBuild.loaderVersion,
    // Die Uhr des Geraets. `ts` wird kurz vor dem Senden noch einmal gesetzt
    // (sendInfoReply), damit der Admin die Abweichung ohne die Laufzeit der
    // Abfrage rechnen kann.
    clock: await clockPromise,
    environment: Diagnostics.environment(),
    screen: readScreen(),
    activity: readActivity(),
    language: (rootState.lang && rootState.lang.active) || null,
    osdLanguage: safeSync(() => Diagnostics.osdLanguage(langKey(rootState))),
    // Die Diagnose-Tabellen der Info-Seite, Abschnitt fuer Abschnitt.
    sections: safeSync(() =>
      Diagnostics.sections({
        diag: castDiag,
        softApVerify: (rootState.cast && rootState.cast.softApVerify) || null,
        langKey: langKey(rootState),
        settings: settings,
      })
    ),
    // Zusaetzlich die Rohdaten: aus den Tabellenzeilen laesst sich nicht
    // weiterrechnen, und ein Admin will u.U. auf einzelne Felder filtern.
    // Ohne die App-Listen - die gibt es auf get_apps.
    cast: trimCast(castDiag),
    // Service-/Pro:Centric-Menue. null, wenn das Geraet nicht rechtzeitig
    // geantwortet hat - dann holt der Admin sie ueber get_settings.
    settings: settings,
  };
}

// Entfernt die App-Listen aus den Cast-Rohdaten und laesst ihre Anzahl stehen.
//
// Ohne das traegt jede get_info-Antwort den kompletten App-Katalog des TV
// mehrfach: einmal als appList/catalog/apps, dann noch als preloadedApps und
// idcapApps. Die Zustandsdaten, um die es bei get_info geht, gingen darin unter.
// Wer die Listen braucht, fragt get_apps.
const CAST_LIST_KEYS = ['appList', 'apps', 'catalog', 'preloadedApps', 'idcapApps'];
function trimCast(diag) {
  if (!diag) return null;
  const caps = diag.capabilities;
  if (!caps) return diag;
  const slim = {};
  const counts = {};
  Object.keys(caps).forEach((k) => {
    if (CAST_LIST_KEYS.indexOf(k) !== -1 && Array.isArray(caps[k])) {
      counts[k] = caps[k].length;
      return;
    }
    slim[k] = caps[k];
  });
  slim.listCounts = counts;
  return Object.assign({}, diag, { capabilities: slim });
}

// Aktive App-Sprache. Eigene Funktion, weil sowohl die OSD-Sprach-Zeile als
// auch die Cast-Tabelle sie brauchen.
function langKey(rootState) {
  const lang = (rootState && rootState.lang) || {};
  return lang.active || null;
}

function safeSync(fn) {
  try {
    return fn();
  } catch (e) {
    return null;
  }
}

export default {
  namespaced: true,
  state: () => ({
    socket: null,
    isSocketConnected: false,
    heartBeat: undefined,
    // Laufendes Firmware-/Clone-Update. Haengt an der state-Meldung mit dran
    // (siehe reportState) - der Socket-Server leitet nur `state` an Admins
    // weiter, ein eigener Kanal haette eine Server-Aenderung gebraucht.
    // null = kein Rollout aktiv.
    rollout: null,
    // Stand des Clonefile-Einsammelns. Faehrt wie der Rollout in der
    // state-Meldung mit - aus demselben Grund: der Socket-Server leitet nur
    // `state` an Admins weiter.
    cloneUpload: null,
  }),
  mutations: {
    setSocket(state, socket) {
      state.socket = socket;
    },
    setState(state, { name, value }) {
      state[name] = value;
    },
    setSocketConnected(state, status) {
      state.isSocketConnected = status;
    },
    setHeartBeat(state, interval) {
      state.heartBeat = interval;
    },
    setCloneUpload(state, value) {
      state.cloneUpload = value;
    },
    setRollout(state, rollout) {
      state.rollout = rollout;
    },
  },
  actions: {
    init({ commit, dispatch, rootState, state }) {
      // Idempotent: bereits verbunden ODER gerade im Aufbau -> keinen zweiten
      // Socket oeffnen. init wird u.a. im Router-Guard bei Navigation
      // angestossen; ohne readyState-Pruefung wuerde ein zweiter Aufruf
      // waehrend der CONNECTING-Phase eine zweite Verbindung erzeugen, die sich
      // mit der ersten per SameId (4003) gegenseitig kickt.
      if (
        state.socket &&
        (state.socket.readyState === WebSocket.CONNECTING ||
          state.socket.readyState === WebSocket.OPEN)
      ) {
        return Promise.resolve('Socket already active');
      }

      const url = `${process.env.SOCKET_BASE}/socket/client/${rootState.groupId}/${rootState.buildingId}/tv/${rootState.deviceId}`;
      const socket = new WebSocket(url);
      commit('setSocket', socket);
      logSocket = socket; // stream Live-Log over the current socket

      socket.onopen = function(event) {
        // Falls dieser Socket zwischenzeitlich verdraengt wurde -> schliessen.
        if (state.socket !== socket) { try { socket.close(); } catch (e) { /* noop */ } return; }
        console.log('SOCKET OPEN', event);
        commit('setSocketConnected', true);
        // Dem Server sagen, dass wir die Kommando-Id zurueckgeben koennen.
        // Ein Server, der das nicht kennt, ignoriert die Nachricht und
        // schickt weiter die alte Form - beides wird unten erkannt.
        try {
          socket.send(JSON.stringify(['capabilities', { commandId: true }]));
        } catch (e) {
          console.log('[socket] capabilities nicht gesendet:', e);
        }
        dispatch('heartBeat');
        dispatch('reportState'); // push initial state on connect
        // LG: die Bildschirm-Sonde laeuft mit der Verbindung an und meldet
        // jede Aenderung sofort weiter (siehe helpers/LgScreen).
        //
        // Und sie loest das Aufraeumen beim AUFWACHEN aus, wenn sie einen
        // Wechsel 'off' -> 'on' gemessen hat.
        //
        // WARUM ZUSAETZLICH ZU main.js: dort haengt der Wake-Pfad an
        // visibilitychange, und darauf ist auf LG kein Verlass. Am 43UK660H ist
        // jetzt belegt, dass power_mode_changed feuert und getPowerMode danach
        // zwischen 2 (WARM, aus) und 1 (NORMAL, an) wechselt - ein GEMESSENER
        // Zustandswechsel also, und damit der verlaesslichere Ausloeser. Lief
        // der Weg ueber visibilitychange schon, ist das hier ein No-op:
        // clearGuestCredentials laeuft nur bei offenem clearPending und haelt
        // seinen eigenen Lauf fest.
        LgScreen.start((s) => {
          dispatch('reportState');
          const now = s && s.screen;
          if (lastLgScreen === 'off' && now === 'on') {
            console.log('[LG] Bildschirm wieder an (gemessen) - Aufraeumen pruefen');
            dispatch('cast/noteStandbyIfSoftApDown', null, { root: true })
              .catch(() => false)
              .then(() => dispatch('cast/clearGuestCredentials', null, { root: true }))
              .catch(() => undefined);
          }
          if (now) lastLgScreen = now;
        });
      };
      socket.onerror = function(event) {
        console.log('SOCKET ERR', event);
      };
      socket.onclose = function(event) {
        // Nur der AKTUELLE Socket darf den State kippen und reconnecten. Ein
        // veralteter (verdraengter) Socket wird ignoriert, sonst schaukelt sich
        // die SameId-Kickschleife endlos auf.
        if (state.socket !== socket) return;
        console.log('SOCKET CLOSED', event);
        commit('setSocketConnected', false);
        dispatch('clearHeartBeat');
        setTimeout(() => {
          dispatch('init');
        }, 2000);
      };
      /*state.socket.onmessage = function(event) {
        console.log('MESSAGE', JSON.parse(event.data));
        const data = JSON.parse(event.data);
        if (data[0] === 'experienceEvent') {
          dispatch('handleSocketCall', data);
        }
        // --- Heartbeat Handling ---
        if (Object.keys(data).length === 0) {
          console.log('Heartbeat {} empfangen');
          try {
            state.socket.send('{}');
            console.log('Heartbeat {} gesendet');
          } catch (e) {
            console.error('Fehler beim Heartbeat-Senden:', e);
          }
          return;
        }
      };*/
      socket.onmessage = function(event) {
        // Nachrichten eines verdraengten Sockets ignorieren.
        if (state.socket !== socket) return;
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (e) {
          console.warn("Ungültige Nachricht:", event.data);
          return;
        }

        // Heartbeat erkennen (leeres Objekt/Array) — protokollkonform als Array antworten
        if (data && typeof data === 'object' && Object.keys(data).length === 0) {
          try {
            state.socket.send(JSON.stringify(['ping']));
          } catch (e) {
            console.error("Fehler beim Heartbeat-Antworten:", e);
          }
          return;
        }

        if (!Array.isArray(data)) return;

        // Live-Log tunnel (vom Admin über den Socket-Server)
        if (data[0] === 'log:open') {
          startLogForward();
          return;
        }
        if (data[0] === 'log:close') {
          stopLogForward();
          return;
        }

        // Kommandos verarbeiten UND dem Socket-Server antworten (Round-Trip).
        // Der command-Kanal erwartet eine Antwort auf demselben Event-Namen;
        // ohne Ack läuft er in "command response timed out" (Code 4004).
        if (data[0] === 'experienceEvent') {
          // Zwei Formen: mit Kommando-Id (['experienceEvent', id, nutzlast])
          // und ohne (['experienceEvent', nutzlast]).
          const withId = typeof data[1] === 'string' && UUID_LIKE.test(data[1]);
          const body = withId ? data[2] : data[1];
          const action = body && body.action;
          if (action) {
            commandChannels[action] = 'experienceEvent';
            if (withId) commandIds[action] = data[1];
            else delete commandIds[action];
          }
          // get_info: gather the Info-page snapshot asynchronously and reply
          // WITH the data (its own round-trip ack), instead of the generic ack.
          if (action === SOCKET_ACTIONS.GET_INFO) {
            dispatch('sendInfoReply');
            return;
          }
          // get_apps genauso: die Antwort TRAEGT die Daten und ist damit ihr
          // eigener Ack - der generische Ack wuerde die Nutzlast verwerfen.
          if (action === SOCKET_ACTIONS.GET_APPS) {
            dispatch('sendAppsReply');
            return;
          }
          // Die geraeteinternen Einstellungen (Service-/Pro:Centric-Menue).
          // Beide Antworten TRAGEN die Werte und sind damit ihr eigener Ack -
          // der generische wuerde die Nutzlast verwerfen. Beim Setzen kommt der
          // Stand NACH dem Setzen zurueck, nicht der geschickte Wunschwert.
          if (action === SOCKET_ACTIONS.GET_SETTINGS) {
            dispatch('sendSettingsReply', body);
            return;
          }
          if (action === SOCKET_ACTIONS.SET_SETTINGS) {
            dispatch('runSetSettings', body);
            return;
          }
          // Die Antwort traegt die Uhr vorher und nachher - eigener Ack.
          if (action === SOCKET_ACTIONS.SET_TIME) {
            dispatch('runSetTime', body);
            return;
          }
          if (action === SOCKET_ACTIONS.GET_TIME) {
            dispatch('sendTimeReply');
            return;
          }
          // Auch hier traegt die Antwort das Ergebnis - Dateiname und Groesse,
          // oder der Grund, warum nichts gefunden wurde.
          if (action === SOCKET_ACTIONS.UPLOAD_CLONE) {
            dispatch('runUploadClone', body);
            return;
          }
          // Das Inventar traegt seine Daten selbst und ist damit sein eigener
          // Ack - anders als upload_clone passt es in das Zeitfenster, weil es
          // nur auflistet und nichts liest oder hochlaedt.
          if (action === SOCKET_ACTIONS.LIST_CLONE) {
            dispatch('sendCloneListReply');
            return;
          }
          if (action === SOCKET_ACTIONS.GET_FILES) {
            dispatch('sendFilesReply', body);
            return;
          }
          let ack = { ok: true, action };
          try {
            // Die alten Empfaenger erwarten die Nachricht in der alten Form.
            dispatch('handleSocketCall', withId ? ['experienceEvent', body] : data);
          } catch (e) {
            ack = { ok: false, action, error: String((e && e.message) || e) };
          }
          try {
            if (state.socket && state.socket.readyState === WebSocket.OPEN) {
              state.socket.send(
                JSON.stringify(withId ? ['experienceEvent', data[1], ack] : ['experienceEvent', ack])
              );
            }
          } catch (e) {
            // ignore
          }
          return;
        }

        // Der zweite Kommando-Kanal: ['command', …].
        //
        // Am Geraet gemessen: ein ueber den Admin abgesetztes Kommando lief in
        // "command response timed out" (Code 4004), und die Fehlermeldung des
        // Servers nannte cmd0='command', cmd1='take_checkout_snapshot'. Genau
        // dieser Rahmen fiel hier bisher durch - der Empfaenger kannte nur
        // 'experienceEvent', also antwortete niemand. Betroffen war JEDES so
        // abgesetzte Kommando, auch clear_credentials.
        //
        // Das genaue Format ist nicht dokumentiert, deshalb wird es tolerant
        // gelesen statt geraten: die Aktion ist der erste String, der keine
        // UUID ist, die Kommando-Id die erste UUID, die Nutzlast das erste
        // Objekt. Der Rohrahmen steht im Log - passt die Zuordnung nicht, ist
        // sie daran abzulesen und nicht erneut zu erraten.
        if (data[0] === 'command') {
          let cmdId = null;
          let cmdAction = null;
          let cmdBody = null;
          for (let i = 1; i < data.length; i += 1) {
            const part = data[i];
            if (typeof part === 'string') {
              if (!cmdId && UUID_LIKE.test(part)) cmdId = part;
              else if (!cmdAction) cmdAction = part;
            } else if (part && typeof part === 'object' && !cmdBody) {
              cmdBody = part;
            }
          }
          const context = Object.assign({}, cmdBody || {});
          if (!context.action && cmdAction) context.action = cmdAction;
          if (!cmdId && typeof context.commandId === 'string') cmdId = context.commandId;
          console.log(
            '[socket] command-Rahmen: aktion=' + (context.action || '?') +
            ' id=' + (cmdId || '-') + ' roh=' + JSON.stringify(data).slice(0, 400)
          );
          if (!context.action) return;
          commandChannels[context.action] = 'command';
          if (cmdId) commandIds[context.action] = cmdId;
          let cmdAck = { ok: true, action: context.action };
          try {
            dispatch('handleActionCall', context);
          } catch (e) {
            cmdAck = { ok: false, action: context.action, error: String((e && e.message) || e) };
          }
          // Auf DEMSELBEN Kanal antworten, auf dem gefragt wurde.
          try {
            if (state.socket && state.socket.readyState === WebSocket.OPEN) {
              state.socket.send(
                JSON.stringify(cmdId ? ['command', cmdId, cmdAck] : ['command', cmdAck])
              );
            }
          } catch (e) {
            // ignore
          }
          return;
        }

        // Der Rahmen, den der Socket-Server tatsaechlich schickt:
        //
        //   ["clear_credentials","0a312ee0-b35a-11f1-916e-9578f926af5d",{"value":"SNAPSHOT"}]
        //    ^ Aktion            ^ Kommando-Id                          ^ Nutzlast
        //
        // Am Geraet gemessen. Weder 'experienceEvent' noch 'command' steht
        // vorn - der Aktionsname SELBST ist das erste Element. Das
        // ["command", …] aus der Admin-Anzeige ist die Richtung Admin ->
        // Server und kommt hier nie an.
        //
        // Erkannt wird das an der Id an zweiter Stelle: ein Array aus String
        // plus UUID ist eindeutig ein Kommando und nichts sonst.
        if (typeof data[0] === 'string' && typeof data[1] === 'string' && UUID_LIKE.test(data[1])) {
          const cmdAction = data[0];
          const cmdId = data[1];
          const body = data[2] && typeof data[2] === 'object' ? data[2] : {};
          const context = Object.assign({ action: cmdAction }, body);
          console.log(
            '[socket] Kommando ' + cmdAction + ' (id ' + cmdId + '): ' + JSON.stringify(body)
          );
          // Der Kanal ist hier der Aktionsname - die Antwort muss denselben
          // Rahmen tragen wie die Frage (siehe commandChannels).
          commandChannels[cmdAction] = cmdAction;
          commandIds[cmdAction] = cmdId;
          let cmdAck = { ok: true, action: cmdAction };
          try {
            dispatch('handleActionCall', context);
          } catch (e) {
            cmdAck = { ok: false, action: cmdAction, error: String((e && e.message) || e) };
          }
          // Sofort quittieren, damit der Server nicht in den Timeout laeuft.
          // Kommandos mit eigener Antwort (sendReply) schicken ihr Ergebnis
          // danach hinterher - so wie auf dem experienceEvent-Kanal auch.
          try {
            if (state.socket && state.socket.readyState === WebSocket.OPEN) {
              state.socket.send(JSON.stringify([cmdAction, cmdId, cmdAck]));
            }
          } catch (e) {
            // ignore
          }
          return;
        }

        // Alles andere: einmal sichtbar machen, statt still zu verwerfen. Genau
        // dieses stille Verwerfen hat den Kommando-Rahmen oben so lange
        // verdeckt.
        console.log('[socket] unbehandelter Rahmen: ' + JSON.stringify(data).slice(0, 400));
      };
      return Promise.resolve();
    },

/*
  heartBeat({ state, commit }) {
    // alle 30 Sekunden einen Heartbeat senden
    const interval = setInterval(() => {
      if (state.isSocketConnected && state.socket) {
        try {
          // entweder Protokoll-Ping
          state.socket.send('{}'); 
          // oder wenn du auf Protokollebene willst:
          // state.socket.ping() <-- geht aber in Browser nicht, nur Node!
        } catch (e) {
          console.error('Heartbeat failed:', e);
        }
      }
    }, 30000);

    commit('setHeartBeatInterval', interval);
  },

  clearHeartBeat({ state, commit }) {
    if (state.heartBeatInterval) {
      clearInterval(state.heartBeatInterval);
      commit('setHeartBeatInterval', null);
    }
  }
*/


    heartBeat({ state, commit }) {
      if (state.heartBeat) return;
      const heartBeat = setInterval(() => {
        // Protokollkonformer Heartbeat: Array-Frame statt '{}'. Der Socket-Server
        // hat zwar eigenes Ping/Pong, aber dieser App-Heartbeat hält Verbindungen
        // durch Proxys offen. ["ping"] ist ein gültiges Array → wird serverseitig
        // ohne Handler geräuschlos ignoriert (keine "unexpected message shape").
        try {
          if (state.socket && state.socket.readyState === WebSocket.OPEN) {
            state.socket.send(JSON.stringify(['ping']));
          }
        } catch (e) {
          // ignore
        }
      }, 40000);
      commit('setHeartBeat', heartBeat);
    },
    clearHeartBeat({ state, commit }) {
      clearInterval(state.heartBeat);
      commit('setHeartBeat', undefined);
    },
    // Push the light live state (screen / activity / language) to the server.
    reportState({ state, rootState }) {
      if (!state.socket || state.socket.readyState !== WebSocket.OPEN) return;
      const payload = {
        screen: readScreen(),
        // Ist dieser Wert gemessen oder geraten? screenKnown=false heisst: kein
        // Power-Listener, keine Sonde, kein Merker - 'on' ist dann nur der
        // Rueckfall. Damit laesst sich der Zustand in der Geraeteliste dezent
        // kennzeichnen, statt eine Annahme wie eine Messung aussehen zu lassen.
        // screenSource nennt die Quelle, falls jemand nachsehen will.
        //
        // readScreen() MUSS vorher gelaufen sein - es setzt diese Werte.
        screenKnown: screenInfo.known,
        screenSource: screenInfo.source,
        activity: readActivity(),
        language: (rootState.lang && rootState.lang.active) || null,
        // Laufender Stand, klein genug fuer jede Meldung: so steht er im Admin
        // auch ohne Info-Abruf, und ein Neustart zeigt sich am neuen startedAt.
        appBuild: AppBuild.build,
        appStartedAt: AppBuild.startedAt,
        ts: Date.now(),
      };
      // Auf LG die Rohwerte der Sonde mitschicken - so beantwortet sich pro
      // Modell aus der Ferne, welcher Weg dort ueberhaupt traegt, ohne jemanden
      // vor das Geraet zu schicken (siehe helpers/LgScreen):
      //
      //   powerEvents  > 0  - das Geraet feuert power_mode_changed, der
      //                       Modus ist damit auswertbar (powerModeLive).
      //   powerEvents == 0  - kein Ereignis; powerMode ist nur eine Zahl.
      //   videoMuteUsable   - false heisst: dieses Modell kann isVideoMute
      //                       nicht (z.B. 43UK660H), die Abfrage ist
      //                       eingestellt.
      if (DeviceType.isLG) {
        const lg = LgScreen.raw;
        if (lg.videoMute !== null) payload.videoMute = lg.videoMute;
        if (lg.powerMode !== null) payload.powerMode = lg.powerMode;
        payload.powerModeLive = lg.powerModeLive;
        payload.powerEvents = lg.powerEvents;
        payload.focusEvents = lg.focusEvents;
        // Richtung des letzten Fokuswechsels ('focused' / 'unfocused'). Das
        // Ereignis traegt sie mit - am Geraet gesehen, als YouTube in den
        // Vordergrund kam. Vorerst reine Beobachtung, siehe helpers/LgScreen.
        if (lg.lastFocus) payload.lastFocus = lg.lastFocus;
        payload.videoMuteUsable = lg.videoMuteUsable;
      }
      // Stand des Checkout-Snapshots. Ohne ihn loescht clear_credentials auf LG
      // nichts (siehe helpers/Cast/LG.js) - und die Entscheidung darueber faellt
      // beim Oeffnen der Cast-Seite, also lange bevor jemand den Live-Log
      // aufmacht. Hier steht sie dauerhaft.
      //
      // snapshotBlocker nennt den Grund, wenn er fehlt: meist "Gast am
      // Hotspot", der persistierte Merker eines frueheren Gastes. Von selbst
      // laeuft der Snapshot dann nicht mehr - dafuer gibt es
      // take_checkout_snapshot mit force.
      if (rootState.cast) {
        payload.checkoutSnapshot = !!rootState.cast.snapshotTaken;
        if (rootState.cast.snapshotReason) payload.snapshotBlocker = rootState.cast.snapshotReason;
      }
      // Der Server ersetzt seinen Schnappschuss komplett - deshalb faehrt der
      // Rollout hier mit und nicht in einer eigenen Meldung, sonst wuerde die
      // eine die andere ueberschreiben.
      if (state.rollout) payload.rollout = state.rollout;
      if (state.cloneUpload) payload.cloneUpload = state.cloneUpload;
      // Letzter Fall, in dem eine Fremd-App die App verdeckt hat, ohne dass wir
      // sie gestartet haben oder der TV in den Standby ging (siehe
      // helpers/ForeignApp). Faehrt aus demselben Grund hier mit.
      const foreignApp = ForeignApp.last();
      if (foreignApp) payload.foreignApp = foreignApp;
      try {
        state.socket.send(JSON.stringify(['state', payload]));
      } catch (e) {
        // ignore
      }
    },
    // clear_credentials ausfuehren und das Ergebnis zurueckmelden.
    //
    // Die Rueckmeldung ist der Punkt: auf Geraeten ohne deleteAppPrivateInfoNow
    // kann der Befehl nur scharf schalten, und das Loeschen passiert erst beim
    // naechsten Neustart. Ohne Antwort sah beides am Admin gleich aus.
    runClearCredentials({ state, dispatch }, context) {
      const value = (context && context.value) || 'ON';
      return dispatch('cast/clearCredentialsCommand', value, { root: true })
        .then((result) => {
          sendReply(state, 'clear_credentials', result);
          return result;
        })
        .catch((e) => {
          const detail = String((e && e.message) || e);
          console.log('[socket] clear_credentials fehlgeschlagen:', detail);
          sendReply(state, 'clear_credentials', { ok: false, detail: detail });
          return null;
        });
    },
    // Den Checkout-Snapshot nehmen (nur LG). Siehe cast/takeCheckoutSnapshot -
    // dort steht, warum der Zeitpunkt alles entscheidet und wann die Aktion von
    // sich aus ablehnt. { force: true } uebergeht die Pruefung.
    runTakeCheckoutSnapshot({ state, dispatch }, context) {
      const force = !!(context && context.force);
      return dispatch('cast/takeCheckoutSnapshot', { force: force }, { root: true })
        .then((result) => {
          sendReply(state, 'take_checkout_snapshot', result);
          return result;
        })
        .catch((e) => {
          const detail = String((e && e.message) || e);
          console.log('[socket] take_checkout_snapshot fehlgeschlagen:', detail);
          sendReply(state, 'take_checkout_snapshot', { ok: false, taken: false, detail: detail });
          return null;
        });
    },
    // "App neu laden" - aber geordnet.
    //
    // Vorher stand hier ein nacktes location.reload(true) mitten im
    // Socket-Handler. Auf den meisten Modellen geht das gut, auf einem BU800
    // fror das Geraet ein und kam erst durch einen Watchdog-Neustart von
    // Samsung wieder. Kein Wunder: zum Zeitpunkt des Reloads waren AVPlay und
    // tvwindow noch gebunden, die nativen Listener der App noch registriert und
    // der Socket samt Heartbeat noch offen - die Seite, der das alles gehoert,
    // verschwand einfach darunter weg.
    //
    // Der Reload wird deshalb erst nach dem Abraeumen ausgeloest. Nebeneffekt:
    // die generische Quittung an den Admin geht noch raus, bevor die Seite
    // geht. Vorher wurde sie erst NACH location.reload() gesendet und war ein
    // Rennen gegen den Seitenwechsel - ein gelungener Reload sah im Admin
    // deshalb oft wie ein Timeout aus.
    //
    // Der Boolean an reload() ist entfallen: er wurde aus dem Standard
    // gestrichen und von aktuellen Engines ignoriert. Ob eine frische Kopie
    // kommt, haengt ohnehin allein an den Cache-Headern des Servers - der
    // Parameter hat nur so ausgesehen, als taete er etwas.
    runAppReload({ state, dispatch }) {
      // Jeder Schritt meldet sich VOR der Ausfuehrung.
      //
      // Der Grund ist die Ferndiagnose: haengt das Geraet in einem nativen
      // Aufruf, kommt danach nichts mehr - die letzte sichtbare Zeile benennt
      // dann genau den Schritt, der nicht zurueckkam. Meldete jeder Schritt
      // erst hinterher, waere die Luecke nicht zuzuordnen.
      const step = (name, fn) => {
        console.log('[socket] Reload-Schritt: ' + name);
        try {
          fn();
        } catch (e) {
          console.log('[socket] Reload-Schritt ' + name + ' uebersprungen:', e);
        }
      };

      // 1. Tuner und Videoflaeche freigeben - nur auf der Seite, der sie
      //    gehoeren. tv/kill greift auf den aktiven Kanal zu und wuerde
      //    anderswo ins Leere laufen.
      const route = (router.currentRoute && router.currentRoute.name) || '';
      if (route === 'TVPlayer') step('tv/kill', () => dispatch('tv/kill', null, { root: true }));
      if (route === 'RadioPlayer') step('radio/kill', () => dispatch('radio/kill', null, { root: true }));

      // 2. Den SoftAP-Listener abmelden. Der wird im normalen Betrieb gesetzt,
      //    also gibt es hier auch etwas abzumelden.
      step('SoftAP-Listener', () => dispatch('cast/unwatchSoftAP', null, { root: true }));

      // 3. Firmware- und Clone-Listener NUR, wenn in dieser Sitzung ueberhaupt
      //    ein Rollout lief.
      //
      //    Vorher wurde beides bedingungslos abgemeldet - auf einem BU800
      //    quittierte unsetUpdateFirmwareListener das mit "UnknownError 9999",
      //    weil nie ein Listener gesetzt worden war. Eine empfindliche native
      //    API ohne Anlass anzufassen ist genau das, was man auf einem Geraet
      //    vermeiden will, das ohnehin beim Neuladen einfriert.
      if (state.rollout) {
        step('Firmware-Listener', () => Cast.unsetFirmwareListener());
        step('Clone-Listener', () => Cast.unsetCloneListener());
      }

      // 4. Heartbeat stoppen.
      step('Heartbeat', () => dispatch('clearHeartBeat'));

      // 5. Neu laden.
      //
      //    Die Meldung geht RAUS, bevor die Seite geht: der Live-Log des Admins
      //    laeuft ueber den Socket, alles Spaetere sieht dort niemand mehr.
      //
      //    KEIN socket.close() mehr davor. Der Messlauf am BU800 zeigte, dass
      //    das Abraeumen sauber durchlaeuft und das Geraet erst danach haengt -
      //    uebrig blieben genau close() und der Reload. Die Verbindung geht mit
      //    der Seite ohnehin zu, also faellt der Aufruf weg und eine Unbekannte
      //    mit ihm.
      //
      //    location.replace statt location.reload: der Router laeuft im
      //    Hash-Modus, die Adresse ist also '<datei>#/<route>'. Ohne den Hash
      //    ist das eine echte Navigation auf das Dokument statt eines Reloads -
      //    zwei verschiedene Wege in der Engine, und reload() ist der, bei dem
      //    dieses Modell stehenbleibt. Der Hash MUSS dabei weg: eine Adresse,
      //    die sich nur im Hash unterscheidet, gilt als Sprung im selben
      //    Dokument und wuerde ueberhaupt nicht neu laden.
      //
      //    Nebenwirkung, bewusst in Kauf genommen: die App startet danach auf
      //    ihrer Anfangsseite statt auf der zuletzt geoeffneten. Fuer "App neu
      //    laden" ist das kein Verlust.
      setTimeout(() => {
        const target = String(location.href).split('#')[0];
        console.log('[socket] Abraeumen fertig, App wird jetzt neu geladen: ' + target);
        location.replace(target);
      }, APP_RELOAD_DELAY_MS);
    },

    // Alle Speicher des Geraets leeren und das Ergebnis zurueckmelden.
    //
    // SCHWERWIEGEND: danach sind device_id, bid, room und fingerprint weg, das
    // Geraet muss neu registriert werden. Deshalb fuehrt diese Action NUR aus,
    // wenn die Nutzlast confirm:true traegt. Ein versehentliches
    // {action:'clear_storage'} aus dem Raw-Panel loescht damit nichts, sondern
    // bekommt eine Absage zurueck.
    //
    // Die App laeuft danach bewusst weiter und laedt sich NICHT von selbst neu:
    // ein Geraet, das im selben Moment unaufgefordert neu startet, ist aus der
    // Ferne nicht mehr von einem Absturz zu unterscheiden. Der Admin hat dafuer
    // "App neu laden" und "Neustart".
    //
    // NEBENWIRKUNG, DIE MAN KENNEN MUSS: localStorage.clear() nimmt auch den
    // Merker cast_checkout_snapshot mit. Das ist meistens erwuenscht - nach
    // einem Geraete-Reset ist der Snapshot auf dem TV ohnehin weg, und der
    // naechste Besuch der Cast-Seite legt einen neuen an (cast/init). HCAP hat
    // keine Abfrage, ob ein Snapshot existiert; dieser Merker ist die einzige
    // Quelle, und ihn hier stehen zu lassen waere schlimmer - die App wuerde
    // "Loeschen nachweisbar wirksam" behaupten, waehrend requestCheckout ins
    // Leere greift.
    //
    // ACHTUNG REIHENFOLGE: geleert wird nur UNSER Speicher, nicht die
    // Anmeldungen auf dem TV. Auf einem Geraet, auf dem noch ein Gastkonto
    // liegt, friert der naechste Snapshot genau dieses ein. Also zuerst
    // clear_credentials, dann clear_storage.
    runClearStorage({ state }, context) {
      if (!context || context.confirm !== true) {
        console.log('[socket] clear_storage ohne confirm - nichts geloescht');
        sendReply(state, 'clear_storage', {
          ok: false,
          detail: 'Bestaetigung fehlt (confirm:true erforderlich) - nichts geloescht',
        });
        return;
      }
      let report;
      try {
        report = StorageInspector.clearAll();
      } catch (e) {
        const detail = String((e && e.message) || e);
        console.log('[socket] clear_storage fehlgeschlagen:', detail);
        sendReply(state, 'clear_storage', { ok: false, detail: detail });
        return;
      }
      console.log('[socket] clear_storage ausgefuehrt:', JSON.stringify(report));
      sendReply(state, 'clear_storage', {
        ok: true,
        report: report,
        detail: 'Speicher geleert - Geraet muss neu registriert werden',
      });
    },
    // Gather the full Info-page snapshot and answer the get_info round-trip.
    async sendInfoReply({ state, rootState, dispatch, rootGetters }) {
      let info;
      try {
        // Erkennung neu durchlaufen lassen, wie es die Info-Seite beim Oeffnen
        // auch tut: die Capabilities koennen aus einer frueheren Sitzung
        // stammen, und der SoftAP-Probe-Wert ist nur im Moment der Abfrage
        // aussagekraeftig. Scheitert das, wird mit dem letzten Stand geantwortet
        // statt gar nicht - eine halbe Diagnose ist mehr als keine.
        let castDiag = null;
        try {
          await withTimeout(dispatch('cast/detect', true, { root: true }), INFO_DETECT_TIMEOUT_MS);
        } catch (e) {
          console.log('[socket] get_info: Cast-Erkennung fehlgeschlagen:', e);
        }
        try {
          castDiag = rootGetters['cast/getDiagnostics'];
        } catch (e) {
          console.log('[socket] get_info: Cast-Diagnose nicht lesbar:', e);
        }
        info = await gatherInfo(rootState, castDiag);
      } catch (e) {
        info = { error: String((e && e.message) || e) };
      }
      try {
        if (state.socket && state.socket.readyState === WebSocket.OPEN) {
          if (info && info.clock) info.clock.ts = Date.now();
          const frame = replyFrame('get_info', { ok: true, action: 'get_info', info });
          // Groesse mitloggen: die Antwort traegt jetzt die kompletten
          // Diagnose-Tabellen samt App-Katalog. Bleibt beim Admin nichts an,
          // ist hier zu sehen, ob es an der Nutzlast liegt.
          console.log(
            '[socket] get_info-Antwort: ' + frame.length + ' Zeichen, ' +
            ((info.sections && info.sections.length) || 0) + ' Abschnitte'
          );
          state.socket.send(frame);
        }
      } catch (e) {
        console.log('[socket] get_info-Antwort nicht gesendet:', e);
      }
    },
    // Die App-Listen des Geraets - eigene Anfrage, damit get_info bei den
    // Zustandsdaten bleibt.
    //
    // Auf LG kommen die Listen aus der Cast-Erkennung (CP-Katalog, Preloaded mit
    // NUMERISCHEN Ids, IDCAP mit Token-Status), auf Samsung aus
    // Device.getInstalledApps(). Beides wird mitgeschickt, weil die
    // Preloaded-Ids der einzige Weg sind, Premium-OTT auf LG zu starten, und der
    // IDCAP-Token-Status der einzige Hinweis darauf, dass eine App noch eine
    // Lizenzfreischaltung braucht.
    async sendAppsReply({ state, dispatch, rootGetters }) {
      let payload;
      try {
        let castDiag = null;
        try {
          await withTimeout(dispatch('cast/detect', true, { root: true }), INFO_DETECT_TIMEOUT_MS);
        } catch (e) {
          console.log('[socket] get_apps: Cast-Erkennung fehlgeschlagen:', e);
        }
        try {
          castDiag = rootGetters['cast/getDiagnostics'];
        } catch (e) {
          console.log('[socket] get_apps: Cast-Diagnose nicht lesbar:', e);
        }
        let installedApps = [];
        try {
          installedApps = (await Device.getInstalledApps()) || [];
        } catch (e) {
          installedApps = [];
        }
        const caps = (castDiag && castDiag.capabilities) || {};
        payload = {
          count: installedApps.length,
          // Aufbereitet wie auf der Info-Seite, damit der Admin es direkt
          // anzeigen kann.
          sections: safeSync(() => Diagnostics.appSections({ diag: castDiag, installedApps })),
          // Und die Rohlisten fuer alles, was sich aus Tabellenzeilen nicht
          // weiterverarbeiten laesst.
          installedApps: installedApps,
          catalog: caps.catalog || null,
          preloadedApps: caps.preloadedApps || null,
          idcapApps: caps.idcapApps || null,
        };
      } catch (e) {
        payload = { error: String((e && e.message) || e) };
      }
      try {
        if (state.socket && state.socket.readyState === WebSocket.OPEN) {
          const frame = replyFrame('get_apps', { ok: true, action: 'get_apps', apps: payload });
          console.log(
            '[socket] get_apps-Antwort: ' + frame.length + ' Zeichen, ' + (payload.count || 0) + ' Apps'
          );
          state.socket.send(frame);
        }
      } catch (e) {
        console.log('[socket] get_apps-Antwort nicht gesendet:', e);
      }
    },
    // Die geraeteinternen Einstellungen lesen und antworten (get_settings).
    //
    // Wie bei get_info ist die Antwort ihr eigener Ack. Sie traegt neben den
    // Werten `supported` - was diese Firmware ueberhaupt kennt - und `details`,
    // ueber welchen nativen Weg jeder Wert kam. Ohne beides waere aus der Ferne
    // nicht zu unterscheiden, ob eine Einstellung fehlt oder das Geraet sie
    // nicht hat, und die Kandidaten-Namen im Katalog liessen sich auf echter
    // Hardware nie verifizieren.
    async sendSettingsReply({ state }, context) {
      let settings;
      try {
        settings = await withTimeout(
          DeviceSettings.read({ describe: context && context.describe }),
          SETTINGS_TIMEOUT_MS
        );
        if (!settings) settings = { values: {}, supported: [], error: 'Zeitueberschreitung beim Lesen' };
      } catch (e) {
        settings = { values: {}, supported: [], error: String((e && e.message) || e) };
      }
      console.log(
        '[socket] get_settings: ' + Object.keys(settings.values || {}).length + ' Wert(e), ' +
        ((settings.supported && settings.supported.length) || 0) + ' unterstuetzt'
      );
      sendReply(state, SOCKET_ACTIONS.GET_SETTINGS, { settings: settings });
    },

    // Einzelne Einstellungen setzen (set_settings).
    //
    // Zurueck geht der Stand NACH dem Setzen, nicht der geschickte Wunschwert:
    // manche Firmware quittiert ein setItemValue mit true und uebernimmt den
    // Wert trotzdem nicht. Was nicht durchging, steht in `failed` - der Admin
    // zeigt es an der betroffenen Zeile an, statt "gesetzt" zu melden.
    async runSetSettings({ state }, context) {
      const wanted = (context && context.settings) || {};
      if (!wanted || typeof wanted !== 'object' || !Object.keys(wanted).length) {
        sendReply(state, SOCKET_ACTIONS.SET_SETTINGS, {
          ok: false,
          settings: { values: {} },
          error: 'keine Einstellungen in der Nutzlast (settings fehlt oder leer)',
        });
        return;
      }
      let result;
      try {
        result = await withTimeout(DeviceSettings.write(wanted), SETTINGS_TIMEOUT_MS);
        if (!result) result = { values: {}, failed: {}, error: 'Zeitueberschreitung beim Setzen' };
      } catch (e) {
        result = { values: {}, failed: {}, error: String((e && e.message) || e) };
      }
      console.log(
        '[socket] set_settings: ' + Object.keys(result.values || {}).length + ' uebernommen, ' +
        Object.keys(result.failed || {}).length + ' abgelehnt'
      );
      sendReply(state, SOCKET_ACTIONS.SET_SETTINGS, {
        settings: result,
        // Zusaetzlich flach: der Admin liest die Absagen dort.
        failed: result.failed,
      });
    },

    // Nur die Uhr lesen (get_time) - die Antwort traegt sie und ist ihr
    // eigener Ack.
    async sendTimeReply({ state }) {
      let clock;
      try {
        clock = await withTimeout(Promise.resolve(DeviceClock.read()), INFO_CLOCK_TIMEOUT_MS + 1000);
        if (!clock) clock = { error: 'Zeitueberschreitung beim Lesen der Uhr' };
      } catch (e) {
        clock = { error: String((e && e.message) || e) };
      }
      // Wie bei get_info: ts so spaet wie moeglich, damit die Abweichung im
      // Admin nicht die Laufzeit der Abfrage enthaelt.
      if (clock && !clock.error) clock.ts = Date.now();
      sendReply(state, SOCKET_ACTIONS.GET_TIME, { ok: !clock.error, clock: clock, error: clock.error });
    },

    // Die Uhr des Geraets setzen (set_time).
    //
    // Zurueck geht, ueber welchen Weg es ging, und die Uhr vorher und nachher.
    // Ein ok allein hiesse wenig: manche Firmware quittiert und uebernimmt
    // trotzdem nichts - an `after` ist das abzulesen.
    async runSetTime({ state }, context) {
      let result;
      try {
        result = await withTimeout(DeviceClock.write(context && context.time), SET_TIME_TIMEOUT_MS);
        if (!result) result = { ok: false, error: 'Zeitueberschreitung beim Setzen' };
      } catch (e) {
        result = { ok: false, error: String((e && e.message) || e) };
      }
      console.log('[socket] set_time: ' + JSON.stringify(result));
      sendReply(state, SOCKET_ACTIONS.SET_TIME, { ok: !!result.ok, clock: result, error: result.error });
    },

    /**
     * Speicher und Dateien des Geraets - auf ausdrueckliche Anfrage.
     *
     * Frueher fuhr das in get_info mit. Ein U800F hat sich davon neu
     * gestartet, sobald zum ersten Mal eine echte Datei auf dem Stick lag; das
     * Auflisten allein lief vorher wochenlang unauffaellig. Seitdem gilt: das
     * meistbenutzte Kommando fasst keine Dateien mehr an, und der Lesetest
     * (openStream + readBase64) laeuft nur, wenn die Nutzlast ihn verlangt.
     */
    async sendFilesReply({ state }, context) {
      let files;
      try {
        files = await withTimeout(
          Promise.resolve(StorageProbe.read({ readTest: !!(context && context.readTest) })).catch(
            (e) => ({ error: String((e && e.message) || e) })
          ),
          SETTINGS_TIMEOUT_MS
        );
        if (!files) files = { error: 'Zeitueberschreitung oder auf dieser Plattform nichts zu holen' };
      } catch (e) {
        files = { error: String((e && e.message) || e) };
      }
      sendReply(state, SOCKET_ACTIONS.GET_FILES, { files: files });
    },

    /**
     * Ein Clonefile vom USB-Stick einsammeln und hochladen.
     *
     * Die Antwort ist wie bei get_info ihr eigener Ack: sie sagt, welche Datei
     * gefunden wurde, wie gross sie war und unter welchem Namen sie beim
     * SocketAdmin liegt. Das Lesen und Hochladen kann bei mehreren MB einen
     * Moment dauern - der Ack geht deshalb erst raus, wenn es durch ist.
     */
    async sendCloneListReply({ state }) {
      let result;
      try {
        result = await CloneUpload.inventory();
      } catch (e) {
        result = { ok: false, detail: String((e && e.message) || e) };
      }
      console.log(
        '[socket] list_clone: ' + ((result && result.detail) || '') +
          ' (' + ((result && result.entries && result.entries.length) || 0) + ')'
      );
      sendReply(state, SOCKET_ACTIONS.LIST_CLONE, result);
    },

    async runUploadClone({ state, commit, dispatch }, context) {
      // ZUERST quittieren, dann arbeiten.
      //
      // Der command-Kanal laeuft nach rund 30 Sekunden in "command response
      // timed out" (4004). Suchen, Lesen und Hochladen einer .sclone dauert
      // laenger - vorher stand die Antwort am Ende der Kette und kam nie an,
      // im Admin sah das aus wie "gar keine Rueckmeldung". Der Ack heisst
      // deshalb nur "angenommen"; das Ergebnis faehrt danach in der
      // state-Meldung mit, genau wie der Fortschritt eines Rollouts.
      sendReply(state, SOCKET_ACTIONS.UPLOAD_CLONE, {
        accepted: true,
        detail: 'Suche laeuft - Ergebnis kommt ueber die Zustandsmeldung',
      });

      const report = (value) => {
        commit('setCloneUpload', Object.assign({ ts: Date.now() }, value));
        dispatch('reportState');
      };
      report({ phase: 'suchen' });

      let result;
      try {
        result = await CloneUpload.run({
          url: context && context.url,
          path: context && context.path,
          onProgress: (p) => report(Object.assign({ phase: 'laeuft' }, p)),
        });
      } catch (e) {
        result = { ok: false, detail: String((e && e.message) || e) };
      }
      console.log('[socket] upload_clone: ' + JSON.stringify(result));
      report(Object.assign({ phase: result && result.ok ? 'fertig' : 'fehler' }, result));
    },

    handleSocketCall({ dispatch }, data) {
      console.log('SOCKET CALL: ', data);
      const message = data[0];
      const context = data[1];
      dispatch(
        'notification/socket',
        {
          title: message,
          message: JSON.stringify(context, null, 2),
        },
        { root: true }
      );
      dispatch('handleActionCall', context);
    },
    handleActionCall({ dispatch, state }, context) {
      switch (context.action) {
        case SOCKET_ACTIONS.OPEN_APP:
          dispatch('navigation/handleOpenApp', context, { root: true });
          break;
        case SOCKET_ACTIONS.RUN_VIDEO:
          const response = MediaHandler.assembleSocketVideoQuery(context);
          router.push(response);
          break;
        case SOCKET_ACTIONS.SHOW_MESSAGE:
          dispatch(
            'notification/overlay',
            {
              title: context.title,
              message: context.message,
              type: context.type || 'info',
              timeout: context.timeout,
              confirm: context.confirm,
            },
            { root: true }
          );
          break;
        case SOCKET_ACTIONS.OPEN_WEBSITE:
          router.push(MediaHandler.assembleWebsiteQuery({ url: context.url }));
          break;
        case SOCKET_ACTIONS.OPEN_SOURCE_APP:
          dispatch('navigation/handleOpenSourceApp', context.sourceapp_id, {
            root: true,
          });
          break;
        case SOCKET_ACTIONS.SET_LANGUAGE:
          dispatch('lang/set', context.language, { root: true });
          break;
        case SOCKET_ACTIONS.SET_TV_PACKAGE:
          dispatch('device/update', null, { root: true });
          break;
        case SOCKET_ACTIONS.TURN_OFF:
          dispatch('device/turnOff', null, { root: true });
          break;
        case SOCKET_ACTIONS.TURN_ON:
          dispatch('device/turnOn', null, { root: true });
          break;
        case SOCKET_ACTIONS.OPEN_WELCOME_SCREEN:
          dispatch('welcome/init', context.guest_data, { root: true });
          break;
        case SOCKET_ACTIONS.OPEN_HOME:
          router.push({ name: 'Home' });
          break;
        case SOCKET_ACTIONS.OPEN_PAGE: {
          // Nur die Seiten aus dieser Liste, und nur ueber ihren Namen.
          //
          // Eine Route direkt aus der Nutzlast zu pushen hiesse, dem Admin die
          // Navigation des Geraets zu ueberlassen - inklusive Seiten, die
          // Zustand aendern (Registrierung) oder ohne Parameter gar nicht
          // laufen (Player). Die Info- und die App-Installationsseite waren
          // bisher nur ueber die Farbtasten-Folge vor dem Geraet erreichbar;
          // genau darum geht es hier.
          const pages = {
            info: 'Info',
            appinstall: 'AppInstall',
            cast: 'Cast',
            home: 'Home',
          };
          const name = pages[String((context && context.page) || '').toLowerCase()];
          if (!name) throw new Error('unbekannte Seite: ' + ((context && context.page) || ''));
          console.log('[socket] open_page -> ' + name);
          router.push({ name: name }).catch(() => {});
          break;
        }
        case SOCKET_ACTIONS.REBOOT:
          // Antwort mit Weg und Ergebnis. Tizen liefert sie (Device/Tizen.js),
          // die anderen Plattformen nur "ausgeloest". Ob der TV wirklich neu
          // gestartet hat, zeigt danach appStartedAt.
          Promise.resolve(Device.reboot())
            .then((r) => sendReply(state, SOCKET_ACTIONS.REBOOT, r || { ok: true, detail: 'ausgeloest' }))
            .catch((e) =>
              sendReply(state, SOCKET_ACTIONS.REBOOT, { ok: false, detail: String((e && e.message) || e) })
            );
          break;
        case SOCKET_ACTIONS.UPDATE:
          // Nicht sofort neu laden - erst abraeumen, siehe runAppReload.
          dispatch('runAppReload');
          break;
        case SOCKET_ACTIONS.RUN_CHANNEL:
          dispatch('runChannel', context);
          break;
        // ---- Cast / OTT (Samsung Tizen primaer) --------------------------
        case SOCKET_ACTIONS.OPEN_CAST:
          dispatch('cast/init', null, { root: true });
          router.push({ name: 'Cast' }).catch(() => {});
          break;
        case SOCKET_ACTIONS.CAST_PREPARE:
          // Token init + Google Cast initialisieren + ToS akzeptieren.
          dispatch('cast/init', null, { root: true });
          break;
        case SOCKET_ACTIONS.CONFIGURE_SOFTAP:
          // context: { ssid, key, security, hidden }
          dispatch('cast/configureSoftAP', context, { root: true });
          break;
        case SOCKET_ACTIONS.LAUNCH_APP:
          // context: { app_key } oder { appId }
          dispatch('runCastApp', context);
          break;
        case SOCKET_ACTIONS.FIRMWARE_UPDATE:
          // context: { id, fileName, version, url, totalBytes }
          dispatch('runFirmwareUpdate', context);
          break;
        case SOCKET_ACTIONS.CLONE_UPDATE:
          // Settings-Cloning; kann fehlende Apps via Clonefile nachziehen.
          // context: { path } (Pfad der CloneEncrypted.sclone im lokalen Speicher)
          dispatch('runCloneUpdate', context);
          break;
        case SOCKET_ACTIONS.CLEAR_CREDENTIALS:
          // context: { value: 'ON' | 'OFF' }
          dispatch('runClearCredentials', context);
          break;
        case SOCKET_ACTIONS.TAKE_CHECKOUT_SNAPSHOT:
          // context: { force?: true } - nur LG, siehe SocketHandler.
          dispatch('runTakeCheckoutSnapshot', context);
          break;
        case SOCKET_ACTIONS.CLEAR_STORAGE:
          // context: { confirm: true }
          dispatch('runClearStorage', context);
          break;
        case SOCKET_ACTIONS.SET_TIME:
          // context: { time: {year, month, day, hour, minute, second} }
          dispatch('runSetTime', context);
          break;
        case SOCKET_ACTIONS.GET_TIME:
          dispatch('sendTimeReply');
          break;
        // RUN_SOURCE wird ueber den SocketAdmin nicht benoetigt -> default.
        case SOCKET_ACTIONS.RUN_SOURCE:
        default:
          dispatch(
            'notification/error',
            {
              title: context.action,
              message: JSON.stringify(context, null, 2),
            },
            { root: true }
          );
      }
    },
    // Tunt per Socket-Kommando einen bestimmten Sender (Samsung + LG + Orsay).
    // Stellt zuerst sicher, dass die Kanalliste geladen ist (run_channel kann
    // kommen, waehrend die App noch auf Home ist und nur den Default kennt),
    // setzt dann den Ziel-Index und oeffnet/aktualisiert den TV-Player.
    async runChannel({ commit, dispatch, rootGetters }, context) {
      try {
        await dispatch('tv/prepare', null, { root: true });
      } catch (e) {
        // Weiter mit dem, was bereits geladen ist.
      }
      const channels = rootGetters['tv/getChannels'] || [];
      const idx = findChannelIndex(channels, context);
      if (idx < 0) {
        dispatch(
          'notification/error',
          {
            title: 'run_channel',
            message: 'Kanal nicht gefunden: ' + JSON.stringify(context),
          },
          { root: true }
        );
        return;
      }
      commit('tv/setChannelIndex', idx, { root: true });
      commit('tv/setTargetIndex', idx, { root: true });
      if (router.currentRoute && router.currentRoute.name === 'TVPlayer') {
        // Schon im Player: der Router mountet nicht neu -> direkt umschalten.
        TV.setChannel(channels[idx], false);
      } else {
        router.push({ name: 'TVPlayer' }).catch(() => {});
      }
    },
    // Startet per Socket-Kommando eine OTT-App (Netflix/YouTube/...). Akzeptiert
    // entweder einen bekannten App-Key (app_key) oder eine direkte App-Id.
    runCastApp({ dispatch, rootState }, context) {
      const key = context && (context.app_key || context.key);
      const apps = (rootState.cast && rootState.cast.apps) || [];
      let app = null;
      if (key) app = apps.find((a) => a.key === key) || null;
      if (!app && context && context.appId) {
        app = { name: context.appId, tizenAppId: context.appId, lgAppId: context.appId };
      }
      if (!app) {
        dispatch(
          'notification/error',
          { title: 'launch_app', message: 'App nicht gefunden: ' + JSON.stringify(context) },
          { root: true }
        );
        return;
      }
      dispatch('cast/prepare', null, { root: true });
      dispatch('cast/launchApp', app, { root: true }).catch(() => {});
    },
    // Firmware-Update. Die .bem-Datei muss unter context.url erreichbar sein
    // (Upload in den internen Speicher spaeter ueber den SocketAdmin).
    // Rollout-Zustand setzen und (gedrosselt) an den Admin melden.
    //
    // `force` fuer Phasenwechsel, Abschluss und Fehler - die sollen sofort
    // ankommen. Reine Prozentschritte warten ROLLOUT_REPORT_MS ab.
    // `reset` beginnt einen neuen Lauf, sonst wird in den bestehenden Stand
    // hineingemischt - so kann der Fortschritt aus dem cast-Modul kommen, ohne
    // Art und Dateiname mitschleppen zu muessen.
    noteRollout({ state, commit, dispatch }, { rollout, force, reset }) {
      const next = reset ? rollout : Object.assign({}, state.rollout || {}, rollout);
      commit('setRollout', next);
      const now = Date.now();
      if (!force && now - lastRolloutReport < ROLLOUT_REPORT_MS) return;
      lastRolloutReport = now;
      dispatch('reportState');
    },

    // Firmware-Update starten.
    //
    // Samsung laedt selbst von der uebergebenen Url. Auf LG hat
    // startManualUpdate KEINEN Url-Parameter - dort loest der Befehl nur das
    // Update aus, die Quelle steht in den Geraeteeinstellungen. Deshalb ist die
    // Url auf LG optional.
    runFirmwareUpdate({ dispatch }, context) {
      const isLG = DeviceType.isLG;
      if (!isLG && (!context || !context.url || !context.fileName)) {
        dispatch('noteRollout', {
          force: true,
          rollout: {
            kind: 'firmware',
            phase: 'error',
            error: 'url/fileName fehlt',
            ts: Date.now(),
          },
        });
        dispatch(
          'notification/error',
          { title: 'firmware_update', message: 'url/fileName fehlt: ' + JSON.stringify(context) },
          { root: true }
        );
        return;
      }
      const fileName = (context && context.fileName) || (context && context.url ? fileNameFromUrl(context.url) : '');
      const base = { kind: 'firmware', fileName: fileName, version: (context && context.version) || null };

      dispatch('noteRollout', {
        force: true,
        reset: true,
        rollout: Object.assign({}, base, { phase: 'start', progress: 0, ts: Date.now() }),
      });

      dispatch('cast/updateFirmware', context, { root: true })
        .then(() => {
          dispatch('noteRollout', {
            force: true,
            rollout: Object.assign({}, base, { phase: 'running', progress: 0, ts: Date.now() }),
          });
          dispatch(
            'notification/new',
            { title: 'Firmware Update', message: 'gestartet: ' + fileName },
            { root: true }
          );
        })
        .catch((err) => {
          const detail = String((err && err.message) || err);
          dispatch('noteRollout', {
            force: true,
            rollout: Object.assign({}, base, { phase: 'error', error: detail, ts: Date.now() }),
          });
          dispatch(
            'notification/error',
            { title: 'Firmware Update', message: detail },
            { root: true }
          );
        });
    },

    // Settings-Cloning (kann fehlende Apps ueber das Clonefile nachinstallieren).
    //
    // Zwei Wege, weil die Plattformen sich unterscheiden:
    //   url  - LG nimmt sie direkt; auf Samsung wird die Datei erst
    //          heruntergeladen, weil restoreCloneData einen lokalen Pfad will.
    //   path - die Datei liegt bereits auf dem Geraet (USB). Funktioniert
    //          ueberall und ist der Rueckfall, wenn der Download scheitert.
    async runCloneUpdate({ dispatch }, context) {
      const url = context && context.url;
      let path = context && (context.path || context.cloneFilePath);
      if (!path && !url) {
        dispatch('noteRollout', {
          force: true,
          rollout: { kind: 'clone', phase: 'error', error: 'path/url fehlt', ts: Date.now() },
        });
        dispatch(
          'notification/error',
          { title: 'clone_update', message: 'path/url fehlt: ' + JSON.stringify(context) },
          { root: true }
        );
        return;
      }
      const base = {
        kind: 'clone',
        fileName: (context && context.fileName) || (url ? fileNameFromUrl(url) : path),
      };

      // Samsung mit Url: erst herunterladen. LG kann die Url selbst aufloesen,
      // dort bleibt sie stehen und wird unten durchgereicht.
      if (!path && url && !DeviceType.isLG) {
        dispatch('noteRollout', {
          force: true,
          reset: true,
          rollout: Object.assign({}, base, { phase: 'download', progress: 0, ts: Date.now() }),
        });
        try {
          path = await Download.toLocalFile(url, context && context.fileName, (p) => {
            dispatch('noteRollout', {
              rollout: Object.assign({}, base, {
                phase: 'download',
                progress: p.percent,
                received: p.received,
                total: p.total,
                ts: Date.now(),
              }),
            });
          });
        } catch (e) {
          const detail = String((e && e.message) || e);
          dispatch('noteRollout', {
            force: true,
            rollout: Object.assign({}, base, { phase: 'error', error: detail, ts: Date.now() }),
          });
          dispatch(
            'notification/error',
            { title: 'Clone Update', message: 'Download: ' + detail },
            { root: true }
          );
          return;
        }
      }

      const target = path || url;
      dispatch('noteRollout', {
        force: true,
        reset: true,
        rollout: Object.assign({}, base, { phase: 'apply', progress: 0, ts: Date.now() }),
      });

      return dispatch('cast/restoreClone', target, { root: true })
        .then(() => {
          dispatch('noteRollout', {
            force: true,
            rollout: Object.assign({}, base, { phase: 'done', progress: 100, ts: Date.now() }),
          });
          dispatch(
            'notification/new',
            { title: 'Clone Update', message: 'angewendet: ' + target },
            { root: true }
          );
        })
        .catch((err) => {
          const detail = String((err && err.message) || err);
          dispatch('noteRollout', {
            force: true,
            rollout: Object.assign({}, base, { phase: 'error', error: detail, ts: Date.now() }),
          });
          dispatch(
            'notification/error',
            { title: 'Clone Update', message: detail },
            { root: true }
          );
        });
    },
  },
  getters: {},
};



// WEBPACK FOOTER //
// ./src/store/modules/socket.js